import { createTheme, adaptV4Theme } from '@mui/material/styles';
import { appBar } from 'theme/overrides/app-bar';
import { backdrop } from 'theme/overrides/backdrop';
import { baseline } from 'theme/overrides/baseline';
import { button } from 'theme/overrides/button';
import { card } from 'theme/overrides/card';
import { cardActions } from 'theme/overrides/card-actions';
import { cardContent } from 'theme/overrides/card-content';
import { cardHeader } from 'theme/overrides/card-header';
import { checkbox, checkboxSwitch } from 'theme/overrides/checkbox';
import { chip } from 'theme/overrides/chip';
import { circularProgress } from 'theme/overrides/circular-progress';
import { collapse } from 'theme/overrides/collapse';
import { container } from 'theme/overrides/container';
import { dialog } from 'theme/overrides/dialog';
import { dialogActions } from 'theme/overrides/dialog-actions';
import { dialogContent } from 'theme/overrides/dialog-content';
import { dialogTitle } from 'theme/overrides/dialog-title';
import { drawer } from 'theme/overrides/drawer';
import { formControl } from 'theme/overrides/form-control';
import { formLabel } from 'theme/overrides/form-label';
import { grid } from 'theme/overrides/grid';
import { iconButton } from 'theme/overrides/icon-button';
import { inputAdornment } from 'theme/overrides/input-adornment';
import { inputBase } from 'theme/overrides/input-base';
import { inputLabel } from 'theme/overrides/input-label';
import { linearProgress } from 'theme/overrides/linear-progress';
import { link } from 'theme/overrides/link';
import { list } from 'theme/overrides/list';
import { listItem } from 'theme/overrides/list-item';
import { menu } from 'theme/overrides/menu';
import { menuItem } from 'theme/overrides/menu-item';
import { outlinedInput } from 'theme/overrides/outlined-input';
import { paper } from 'theme/overrides/paper';
import { popover } from 'theme/overrides/popover';
import { radio } from 'theme/overrides/radio';
import { select } from 'theme/overrides/select';
import { tab } from 'theme/overrides/tab';
import { table } from 'theme/overrides/table';
import { tableCell } from 'theme/overrides/table-cell';
import { tableContainer } from 'theme/overrides/table-container';
import { tableRow } from 'theme/overrides/table-row';
import { tabs } from 'theme/overrides/tabs';
import { toolbar } from 'theme/overrides/toolbar';
import { tooltip } from 'theme/overrides/tooltip';
import { cssVar } from 'utils/css';

const theme = createTheme(
  adaptV4Theme({
    props: {
      MuiButtonBase: {
        disableRipple: true,
      },
    },
    palette: {
      primary: {
        main: '#24a7ca',
      },
      text: {
        primary: '#434b51',
        secondary: '#b8b8b8',
      },
      background: {
        default: cssVar('--color-hint-of-red'),
      },
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 900,
        lg: 1200,
        xl: 1300,
      },
    },
    typography: {
      fontFamily: ['"Montserrat"', 'sans-serif'].join(','),
      fontSize: 14,
      h1: {
        color: cssVar('--color-mine-shaft'),
        fontSize: 32,
        fontWeight: 700,
        lineHeight: '1.3',
      },
      h2: {
        color: cssVar('--color-mine-shaft'),
        fontSize: 18,
        fontWeight: 700,
        lineHeight: '1.3',
      },
      h3: {
        color: cssVar('--color-mine-shaft'),
        fontSize: 16,
        fontWeight: 700,
        lineHeight: '1.3',
      },
      h4: {
        color: cssVar('--color-mine-shaft'),
        fontSize: 16,
        fontWeight: 400,
        lineHeight: '1.3',
      },
      h5: {
        color: cssVar('--color-mine-shaft'),
        fontSize: 16,
        fontWeight: 700,
        lineHeight: '1.3',
      },
      h6: {
        color: cssVar('--color-mine-shaft'),
        fontSize: 18,
        fontWeight: 700,
        lineHeight: '1.3',
      },
      body1: {
        fontSize: 14,
        lineHeight: '1.6',
      },
      body2: {
        fontSize: 12,
        lineHeight: '1.6',
      },
      caption: {
        fontSize: 10,
        lineHeight: '1.6',
      },
      button: {
        textTransform: 'none',
      },
    },
    overrides: {
      MuiAppBar: {
        ...appBar,
      },
      MuiCssBaseline: {
        ...baseline,
      },
      MuiBackdrop: {
        ...backdrop,
      },
      MuiButton: {
        ...button,
      },
      MuiCard: {
        ...card,
      },
      MuiCardActions: {
        ...cardActions,
      },
      MuiCardContent: {
        ...cardContent,
      },
      MuiCardHeader: {
        ...cardHeader,
      },
      MuiCheckbox: {
        ...checkbox,
      },
      MuiChip: {
        ...chip,
      },
      MuiCircularProgress: {
        ...circularProgress,
      },
      MuiCollapse: {
        ...collapse,
      },
      MuiContainer: {
        ...container,
      },
      MuiDialog: {
        ...dialog,
      },
      MuiDialogActions: {
        ...dialogActions,
      },
      MuiDialogContent: {
        ...dialogContent,
      },
      MuiDialogTitle: {
        ...dialogTitle,
      },
      MuiDrawer: {
        ...drawer,
      },
      MuiFormControl: {
        ...formControl,
      },
      MuiFormLabel: {
        ...formLabel,
      },
      MuiGrid: {
        ...grid,
      },
      MuiIconButton: {
        ...iconButton,
      },
      MuiInputAdornment: {
        ...inputAdornment,
      },
      MuiInputBase: {
        ...inputBase,
      },
      MuiInputLabel: {
        ...inputLabel,
      },
      MuiLinearProgress: {
        ...linearProgress,
      },
      MuiLink: {
        ...link,
      },
      MuiList: {
        ...list,
      },
      MuiListItem: {
        ...listItem,
      },
      MuiMenu: {
        ...menu,
      },
      MuiMenuItem: {
        ...menuItem,
      },
      MuiOutlinedInput: {
        ...outlinedInput,
      },
      MuiPaper: {
        ...paper,
      },
      MuiPopover: {
        ...popover,
      },
      MuiRadio: {
        ...radio,
      },
      MuiSelect: {
        ...select,
      },
      MuiSwitch: {
        ...checkboxSwitch,
      },
      MuiTab: {
        ...tab,
      },
      MuiTable: {
        ...table,
      },
      MuiTableCell: {
        ...tableCell,
      },
      MuiTableContainer: {
        ...tableContainer,
      },
      MuiTableRow: {
        ...tableRow,
      },
      MuiTabs: {
        ...tabs,
      },
      MuiToolbar: {
        ...toolbar,
      },
      MuiTooltip: {
        ...tooltip,
      },
    },
  })
);

export default theme;
